import {getApi} from '@/api/utils';
import {AppButton} from '@/components/AppButton';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Alert,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import {useState} from 'react';
import {useNotify, useRecordContext} from 'react-admin';
import {useNavigate} from 'react-router-dom';

export const ChangePasswordButton = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleOpen = () => {
        setOpen(true);
        setPassword('');
        setConfirmPassword('');
        setError(null);
    };

    const handleClose = () => {
        if (!loading) {
            setOpen(false);
        }
    };

    const handleSubmit = async () => {
        setError(null);

        if (password.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);
        try {
            if (!record?.id) return;
            const api = getApi(navigate);
            await api.setUserPassword(record.id as string, password);
            notify('Password updated successfully', {type: 'success'});
            setOpen(false);
        } catch (err: any) {
            const message =
                err?.response?.data?.message || err?.message || 'Failed to update password';
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    if (!record) return null;

    return (
        <>
            <AppButton
                variant="secondary"
                size="small"
                startIcon={<LockResetIcon />}
                onClick={handleOpen}
            >
                Change Password
            </AppButton>
            <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
                <DialogTitle>
                    Change Password for {record.first_name} {record.last_name}
                </DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{mb: 2}}>
                            {error}
                        </Alert>
                    )}
                    <TextField
                        autoFocus
                        margin="dense"
                        label="New Password"
                        type="password"
                        fullWidth
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={loading}
                        inputProps={{minLength: 8}}
                    />
                    <TextField
                        margin="dense"
                        label="Confirm Password"
                        type="password"
                        fullWidth
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={loading}
                        error={confirmPassword.length > 0 && password !== confirmPassword}
                        helperText={
                            confirmPassword.length > 0 && password !== confirmPassword
                                ? 'Passwords do not match'
                                : ''
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <AppButton
                        variant="ghost"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Cancel
                    </AppButton>
                    <AppButton
                        variant="primary"
                        onClick={handleSubmit}
                        loading={loading}
                        disabled={!password || !confirmPassword}
                    >
                        Update Password
                    </AppButton>
                </DialogActions>
            </Dialog>
        </>
    );
};
